import React from 'react';

// Icon Components
const HeartIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
);

const SparkleIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"></path>
        <path d="M5 19l.5 1.5L7 21l-1.5.5L5 23l-.5-1.5L3 21l1.5-.5L5 19z"></path>
        <path d="M19 13l.5 1.5L21 15l-1.5.5L19 17l-.5-1.5L17 15l1.5-.5L19 13z"></path>
    </svg>
);

const LeafIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path>
        <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>
    </svg>
);

const UsersIcon = () => (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
);

// Default icons array for cards
const defaultIcons = [HeartIcon, SparkleIcon, LeafIcon, UsersIcon];

// Default Core Values Data (fallback)
const defaultCoreValues = [
    {
        icon: HeartIcon,
        title: 'Taste of Home',
        description: 'Authentic, comforting flavors that remind you of your mother\'s cooking. Every dish is prepared with traditional recipes and the same care as a home kitchen.',
    },
    {
        icon: SparkleIcon,
        title: 'Uncompromising Hygiene',
        description: 'From our hygienic kitchens to your plate, cleanliness is non-negotiable. All our home chefs follow strict food safety protocols, and meals are prepared fresh after each order.',
    },
    {
        icon: LeafIcon,
        title: 'Eco-Consciousness',
        description: 'Sustainable from prep to packaging. We use 100% biodegradable containers, cloth napkins, and deliver in insulated bags to keep food warm—completely plastic-free tiffin service.',
    },
    {
        icon: UsersIcon,
        title: 'Customer Freedom',
        description: 'Flexible subscription plans you can pause or cancel anytime. Choose your delivery time slot, change your address, and enjoy home food delivery that adapts to your life in Gurgaon.',
    }
];

const AboutUs = ({ preloadedData }) => {
    const storyData = preloadedData?.storyData || {
        smallHeading: 'OUR STORY',
        heading: "More Than a Meal, It's the Comfort of Home",
        content: 'BentoBox is a FoodTech company...',
        img: ''
    };

    const missionData = preloadedData?.missionData || {
        smallHeading: 'WHAT WE STAND FOR',
        heading: 'Our Core Values',
        titleMission: 'Our Mission',
        missionDescription: 'To deliver the authentic taste and warmth...'
    };

    const coreValues = preloadedData?.coreValues?.map((card, index) => ({
        ...card,
        icon: defaultIcons[index % defaultIcons.length]
    })) || defaultCoreValues;

    // Parse the heading into two parts for styling
    const parseHeading = (heading) => {
        if (!heading) return { part1: '', part2: '' };
        const commaIndex = heading.indexOf(',');
        if (commaIndex !== -1) {
            return {
                part1: heading.substring(0, commaIndex + 1),
                part2: heading.substring(commaIndex + 1).trim()
            };
        }
        return { part1: heading, part2: '' };
    };

    const { part1, part2 } = parseHeading(storyData.heading);

    return (
        <section id="about" className="!pt-12 md:!pt-16 bg-gradient-to-b from-orange-50/50 to-white">
            {/* Story Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 md:pt-12 md:pb-16">
                {/* Header */}
                <div className="text-center mb-12 md:mb-16">
                    <span className="text-orange-500 font-semibold text-sm tracking-widest uppercase">
                        {storyData.smallHeading}
                    </span>
                    <h1 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                        {part1}{' '}
                        {part2 && <span className="text-orange-500 italic">{part2}</span>}
                    </h1>
                </div>

                {/* Story Content */}
                <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
                    {/* Image */}
                    <div className="w-full lg:w-1/2">
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                            <img
                                src={storyData.img}
                                alt="Home cooking - Traditional Indian kitchen"
                                className="w-full h-64 md:h-80 lg:h-96 object-cover"
                            />
                            {/* Decorative elements */}
                            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-orange-200 rounded-full opacity-50 blur-xl"></div>
                            <div className="absolute -top-4 -left-4 w-16 h-16 bg-orange-300 rounded-full opacity-40 blur-lg"></div>
                        </div>
                    </div>

                    {/* Text Content */}
                    <div className="w-full lg:w-1/2 space-y-5">
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed text-justify">
                            {storyData.content}
                        </p>
                    </div>
                </div>
            </div>

            {/* Orange Mission Section */}
            <div className="bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400 py-12 md:py-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        {missionData.titleMission}
                    </h3>
                    <p className="text-white/95 text-base md:text-lg leading-relaxed italic">
                        {missionData.missionDescription}
                    </p>
                </div>
            </div>

            {/* Core Values Section */}
            <div className="bg-gradient-to-b from-orange-50/30 to-white py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <span className="text-orange-500 font-semibold text-sm tracking-widest uppercase">
                            {missionData.smallHeading}
                        </span>
                        <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">
                            {missionData.heading}
                        </h2>
                    </div>

                    {/* Values Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {coreValues.map((value, index) => (
                            <div
                                key={value.id || index}
                                className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100 hover:shadow-lg hover:border-orange-200 transition-all duration-300 group"
                            >
                                {/* Icon */}
                                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors duration-300">
                                    <div className="text-orange-500">
                                        <value.icon />
                                    </div>
                                </div>

                                {/* Title */}
                                <h4 className="text-lg font-bold text-gray-900 mb-3">
                                    {value.title}
                                </h4>

                                {/* Description */}
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    {value.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutUs;
